/**
 * Referral program & incentive tracking (PE-2190 FE, backend PE-2189) — mirrors osprey-api's
 * `src/domain/referrals/dto/referrals.dto.ts` and the Prisma enums in `referral.model.prisma`.
 * osprey-api owns this data. Money is MINOR units (kobo) throughout — `amountMinor`,
 * `resolvedAmountMinor` — and percentages are plain 0–100 numbers.
 */

export const ReferrerType = {
  MERCHANT: 'MERCHANT',
  STAFF: 'STAFF',
  EXTERNAL_PARTNER: 'EXTERNAL_PARTNER',
} as const;
export type ReferrerType = (typeof ReferrerType)[keyof typeof ReferrerType];

export const RewardType = {
  DISCOUNT: 'DISCOUNT',
  STORE_CREDIT: 'STORE_CREDIT',
  CASH_COMMISSION: 'CASH_COMMISSION',
} as const;
export type RewardType = (typeof RewardType)[keyof typeof RewardType];

export const RewardAmountType = {
  FIXED: 'FIXED',
  PERCENTAGE: 'PERCENTAGE',
} as const;
export type RewardAmountType = (typeof RewardAmountType)[keyof typeof RewardAmountType];

export const RewardTriggerCondition = {
  CONTRACT_SIGNED: 'CONTRACT_SIGNED',
  FIRST_PAYMENT_RECEIVED: 'FIRST_PAYMENT_RECEIVED',
} as const;
export type RewardTriggerCondition =
  (typeof RewardTriggerCondition)[keyof typeof RewardTriggerCondition];

/** Strictly forward, one step at a time — osprey-api 400s any skip or reversal. */
export const RewardLedgerStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  PAID: 'PAID',
} as const;
export type RewardLedgerStatus = (typeof RewardLedgerStatus)[keyof typeof RewardLedgerStatus];

export const REFERRER_TYPE_LABELS: Record<ReferrerType, string> = {
  MERCHANT: 'Merchant',
  STAFF: 'TCS staff',
  EXTERNAL_PARTNER: 'External partner',
};

export const REWARD_TYPE_LABELS: Record<RewardType, string> = {
  DISCOUNT: 'Discount',
  STORE_CREDIT: 'Store credit',
  CASH_COMMISSION: 'Cash commission',
};

export const AMOUNT_TYPE_LABELS: Record<RewardAmountType, string> = {
  FIXED: 'Fixed amount',
  PERCENTAGE: 'Percentage of deal',
};

export const TRIGGER_LABELS: Record<RewardTriggerCondition, string> = {
  CONTRACT_SIGNED: 'Contract signed',
  FIRST_PAYMENT_RECEIVED: 'First payment received',
};

export const LEDGER_STATUS_LABELS: Record<RewardLedgerStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  PAID: 'Paid',
};

export const LEDGER_STATUS_CLASSES: Record<RewardLedgerStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700',
};

export interface ReferralCode {
  id: string;
  code: string;
  referrerType: ReferrerType;
  referrerReferenceId: string | null;
  referrerDisplayName: string;
  referrerEmail: string;
  isActive: boolean;
  rewardProgramId: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReferralCodeQuery {
  referrerType?: ReferrerType;
  isActive?: boolean;
}

export interface CreateReferralCodePayload {
  code: string;
  referrerType: ReferrerType;
  referrerReferenceId?: string;
  referrerDisplayName: string;
  referrerEmail: string;
  rewardProgramId?: string;
}

export interface UpdateReferralCodePayload {
  isActive?: boolean;
  referrerDisplayName?: string;
  referrerEmail?: string;
  rewardProgramId?: string;
}

export interface ReferralAttribution {
  id: string;
  contactId: string;
  referralCodeId: string;
  attributedAt: string;
  createdById: string | null;
}

export interface ReferralRewardProgram {
  id: string;
  name: string;
  isDefault: boolean;
  rewardType: RewardType;
  amountType: RewardAmountType;
  amountMinor: number | null;
  percentage: number | null;
  triggerCondition: RewardTriggerCondition;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReferralRewardProgramPayload {
  name: string;
  isDefault?: boolean;
  rewardType: RewardType;
  amountType: RewardAmountType;
  /** Required when amountType is FIXED. */
  amountMinor?: number;
  /** Required when amountType is PERCENTAGE. */
  percentage?: number;
  triggerCondition: RewardTriggerCondition;
}

export type UpdateReferralRewardProgramPayload = Partial<CreateReferralRewardProgramPayload> & {
  isActive?: boolean;
};

export interface ReferralRewardLedgerEntry {
  id: string;
  attributionId: string;
  rewardType: RewardType;
  amountType: RewardAmountType;
  configuredAmountMinor: number | null;
  configuredPercentage: number | null;
  /** null for a percentage reward recorded without a base deal amount. */
  resolvedAmountMinor: number | null;
  triggerCondition: RewardTriggerCondition;
  triggeredAt: string;
  status: RewardLedgerStatus;
  /** Set for DISCOUNT / STORE_CREDIT — how (or why not) the credit was applied. */
  issuanceNote: string | null;
  approvedById: string | null;
  approvedAt: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
}
